/**
 * Pure logic (no SolidJS or crypto) of vault, isolated for testing:
 *   - lock/unlock state machine,
 *   - item list derivation,
 *   - mapping health report to UI counters.
 */

import type { VaultStatus } from "./vault-context";

interface VaultStatusInputs {
  /** Local DB (PowerSync) is ready. */
  readonly dbReady: boolean;
  /** Account query is still loading. */
  readonly accountLoading: boolean;
  /** A local `vault_account` row exists (setup completed). */
  readonly hasAccount: boolean;
  /** Keys in memory (unlocked). */
  readonly hasKeys: boolean;
}

/** Derives vault state deterministically (same logic as context). */
export function deriveVaultStatus(inputs: VaultStatusInputs): VaultStatus {
  if (!inputs.dbReady || inputs.accountLoading) {
    return "loading";
  }
  if (!inputs.hasAccount) {
    return "needs-setup";
  }
  return inputs.hasKeys ? "unlocked" : "locked";
}

/** A gate state that a user has reached, kept per account (see `holdSettledStatus`). */
export interface SettledVaultStatus {
  readonly userId: string;
  readonly status: "needs-setup" | "locked";
}

/**
 * Keeps a settled gate state through a transient "loading".
 *
 * The account query reports `isLoading` for every re-run while it holds no
 * row, which is also the state of a user who has not set up a vault yet. A
 * background re-run (the DB is re-bound, or a sync write lands) therefore
 * looked like a loading gate, unmounting the setup form and dropping what the
 * user had typed. Once the same user has settled to "needs-setup" or "locked",
 * a transient "loading" keeps that status. Other users and "unlocked" never
 * inherit it.
 */
export function holdSettledStatus(
  derived: VaultStatus,
  userId: string | null,
  settled: SettledVaultStatus | null,
): { readonly status: VaultStatus; readonly settled: SettledVaultStatus | null } {
  if (derived === "needs-setup" || derived === "locked") {
    return { status: derived, settled: userId ? { userId, status: derived } : null };
  }
  if (derived === "loading" && userId !== null && settled?.userId === userId) {
    return { status: settled.status, settled };
  }
  return { status: derived, settled: null };
}

export type VaultItemType = "login" | "note" | "card" | "identity";

export interface ListableItem {
  readonly id: string;
  readonly type: VaultItemType;
  readonly name: string;
  readonly username?: string;
  readonly updatedAt?: string;
}

/**
 * Sorts and filters decrypted items for list: by query (name or
 * username, case-insensitive) then alphabetically by name.
 */
export function deriveItemList(items: ListableItem[], query = ""): ListableItem[] {
  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter(
        (i) => i.name.toLowerCase().includes(q) || (i.username ?? "").toLowerCase().includes(q),
      )
    : items;
  return filtered.toSorted((a, b) => a.name.localeCompare(b.name));
}

/**
 * Formats TOTP code for readability: a 6-digit code splits into
 * two groups of 3 ("123456" → "123 456"). Any other length is returned
 * as is (we do not assume format).
 */
export function formatTotpCode(code: string): string {
  return code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code;
}

/**
 * Real state of item for inventory view. Derives from live security
 * health (weak/reused/old) — NOT a static icon. Priority
 * descendente: `weak` > `reused` > `stale` > `ok`.
 */
export type ItemStatus = "weak" | "reused" | "stale" | "ok";

export interface InventoryReport {
  readonly weak: readonly string[];
  readonly reused: readonly (readonly string[])[];
  readonly old: readonly string[];
}

export interface InventoryRow extends ListableItem {
  /** State derived from actual analysis (not fixed icon by type). */
  readonly status: ItemStatus;
}

/**
 * Derives inventory rows from ACTUAL STATE: combines item list
 * (type, name, last activity) with live health report to tag
 * each item with current state, instead of outdated fixed icon.
 * Items without password (note/identity) stay `ok`.
 */
export function deriveInventory(
  items: ListableItem[],
  report: InventoryReport,
  query = "",
): InventoryRow[] {
  const weak = new Set(report.weak);
  const reused = new Set(report.reused.flat());
  const stale = new Set(report.old);
  return deriveItemList(items, query).map((item) => ({
    ...item,
    status: statusFor(item.id, weak, reused, stale),
  }));
}

function statusFor(
  id: string,
  weak: Set<string>,
  reused: Set<string>,
  stale: Set<string>,
): ItemStatus {
  if (weak.has(id)) {
    return "weak";
  }
  if (reused.has(id)) {
    return "reused";
  }
  if (stale.has(id)) {
    return "stale";
  }
  return "ok";
}

interface HealthSummary {
  readonly total: number;
  readonly weak: number;
  readonly reused: number;
  readonly healthy: number;
}

interface RawHealthReport {
  readonly weak: readonly string[];
  readonly reused: readonly (readonly string[])[];
}

/**
 * Maps raw `crypto.analyzeVaultHealth` report to UI counters.
 * `total` is count of analyzed items with password. `healthy` = those
 * neither weak nor reused.
 */
export function summarizeHealth(report: RawHealthReport, total: number): HealthSummary {
  const weakSet = new Set(report.weak);
  const reusedSet = new Set(report.reused.flat());
  const unhealthy = new Set<string>([...weakSet, ...reusedSet]);
  return {
    total,
    weak: weakSet.size,
    reused: reusedSet.size,
    healthy: Math.max(0, total - unhealthy.size),
  };
}
